import React, { MutableRefObject } from 'react'

// Components
import {
  Breakpoint,
  Skeleton,
  pluralize,
  TabPanel,
  Tabs,
} from '@sketch/components'

// Styles
import {
  StyledTabList,
  RoleTabsContainer,
  StyledTab,
  FiltersWrapper,
} from './RoleTabs.styles'

import { useFlag } from 'containers/flags'

// Types
import {
  GetWorkspaceMembershipsQueryVariables,
  WorkspaceMembershipFragment,
} from '@sketch/gql-types'

import { MembershipsPanelFilters } from '../../hooks/useMembershipsPanel'

type UserCountType = {
  totalMemberships: number | null
  totalGuests: number | null
  totalMembers: number | null
}

interface RoleTabsProps {
  filters: MembershipsPanelFilters
  userCount: MutableRefObject<UserCountType>
}

// Helpers
const getRoleFromTab = (
  tabIndex: number
): WorkspaceMembershipFragment['role'] | undefined => {
  if (tabIndex === 1) {
    return 'MEMBER'
  }

  if (tabIndex === 2) {
    return 'GUEST'
  }

  // an 'undefined' role will reset the filter to all
  return undefined
}

/*
 * Tabs can have 2 options selected at the same time.
 * And for that to work we contain the second set of tabs like so:
 *
 * <TabList>
 *   <Tab>One</Tab>       // can be selected
 *   <div>                // second set of tabs
 *     <Tab>Two-A</Tab>   // can also selected
 *     <Tab>Two-B</Tab>
 *   </div>
 * </TabList>
 *
 * TabList injects a "onTabSelected" prop on its children which is
 * an invalid prop for DOM elements, causing a warning.
 *
 * This component is just a div that forwards its children only
 * and removes all injected props
 */
const TabFilters: React.FC = ({ children }) => {
  return <FiltersWrapper>{children}</FiltersWrapper>
}

/*
 * RoleTabs
 *
 * Renders the filter tabs for the Memberships Table
 */
const RoleTabs: React.FC<RoleTabsProps> = ({ filters, userCount }) => {
  const isUserPermissionsDirectoryFFEnabled = useFlag(
    'user-permissions-directory'
  )
  /** Set the Role filter */
  const handleTabsSelected = (selectedTabIndex: number) => {
    const role = getRoleFromTab(selectedTabIndex)

    // For Members tabs we want to show all the members that are NOT guests
    // This is the reason to treat it differently
    if (role === 'MEMBER') {
      filters.setRoleFilter(undefined)
      filters.setExcludeRolesFilter(['GUEST'])
      return
    }

    // For any other case, we just filter normally
    // Resetting the excluded roles
    filters.setRoleFilter(role)
    filters.setExcludeRolesFilter(undefined)
  }

  /** Set the Access level filter */
  const handleAccessLevelClick = (
    accessLevel: GetWorkspaceMembershipsQueryVariables['accessLevel']
  ) => {
    filters.setAccessLevelFilter(accessLevel)
  }

  const renderAccessLevelTabFilters = () => {
    /** Only render Access Level tab filters when "Guests" is NOT the chosen role */
    if (filters.roleFilter !== 'GUEST') {
      return (
        <TabFilters>
          <StyledTab
            // an 'undefined' access level will reset the filter to all
            onTabSelected={() => handleAccessLevelClick(undefined)}
            selected={filters.accessLevelFilter === undefined}
          >
            <Breakpoint on="md">
              {matches => (matches ? 'View All' : 'All')}
            </Breakpoint>
          </StyledTab>
          <StyledTab
            onTabSelected={() => handleAccessLevelClick('EDITOR')}
            selected={filters.accessLevelFilter === 'EDITOR'}
          >
            Editors
          </StyledTab>
          <StyledTab
            onTabSelected={() => handleAccessLevelClick('VIEWER')}
            selected={filters.accessLevelFilter === 'VIEWER'}
          >
            Viewers
          </StyledTab>
        </TabFilters>
      )
    }

    return null
  }

  const getSelectedTabIndex = (
    selectedRole: WorkspaceMembershipFragment['role'] | null | undefined
  ) => {
    if (selectedRole === 'MEMBER') {
      return 1
    }

    if (selectedRole === 'GUEST') {
      return 2
    }

    return 0
  }

  return (
    <RoleTabsContainer>
      <Tabs
        onTabSelected={handleTabsSelected}
        selectedTabIndex={getSelectedTabIndex(filters.roleFilter)}
      >
        <StyledTabList>
          {/** Role tabs*/}
          <StyledTab>
            {userCount?.current?.totalMemberships !== null ? (
              `${userCount.current.totalMemberships} Total`
            ) : (
              <Skeleton height="10px" width="60px" />
            )}
          </StyledTab>
          {isUserPermissionsDirectoryFFEnabled ? (
            <StyledTab>
              {userCount?.current?.totalGuests !== null ? (
                `${userCount.current.totalMembers} ${pluralize(
                  'Member',
                  'Members',
                  userCount?.current?.totalMembers || 1
                )}`
              ) : (
                <Skeleton height="10px" width="60px" />
              )}
            </StyledTab>
          ) : (
            <></>
          )}
          <StyledTab>
            {userCount?.current?.totalGuests !== null ? (
              `${userCount.current.totalGuests} ${pluralize(
                'Guest',
                'Guests',
                userCount.current.totalGuests
              )}`
            ) : (
              <Skeleton height="10px" width="60px" />
            )}
          </StyledTab>

          {/** Renders the Access Level tabs*/}
          <>{renderAccessLevelTabFilters()}</>
        </StyledTabList>

        {/* We don't care about the panels. Tabs triggger query filters */}
        <TabPanel>
          <></>
        </TabPanel>
      </Tabs>
    </RoleTabsContainer>
  )
}

export default RoleTabs
